/****************************************************************************
 **
 ** Copyright (C) 2021 The Qt Company Ltd.
 ** Contact: https://www.qt.io/licensing/
 **
 ** This file is part of Qt Bridge Plugin.
 **
 ** Commercial License Usage
 ** Licensees holding valid commercial Qt licenses may use this file in
 ** accordance with the commercial license agreement provided with the
 ** Software or, alternatively, in accordance with the terms contained in
 ** a written agreement between you and The Qt Company. For licensing terms
 ** and conditions see https://www.qt.io/terms-conditions. For further
 ** information use the contact form at https://www.qt.io/contact-us.
 **
 ****************************************************************************/

import Path from "path"
import { Library } from "sketch/dom";
import * as UI from "sketch/ui"
import { BUtils } from "./qtbridgeutils.js";
import { Metadata } from "./metadata.js";
import { SLayer } from "./slayer.js";
import {
    SketchLayerType as SLT,
    ValidExportFormats as VEF,
    ConstExportOptions as CEO
} from "./constants.js";


const DocumentMetaDict = {
    jsonExportPath: defaultExportPath,
    uiScaleFactor: defaultUiScaleFactor,
    organizeInPages: () => { return false; },
    scaleFormat: defaultScaleFormat,
    formatOptions: defaultFormatOptions
};

export class SDocument {
    constructor(instance) {
        this._instance = instance;
        this._metadata = new Metadata(instance, DocumentMetaDict);
    }

    instance() {
        return this._instance;
    }

    documentId() {
        try {
            return this._instance.id;
        } catch (e) {
            BUtils.logError(`Can not get layer property (id). ${e}`);
        }
    }

    documentName() {
        try {
            let docPath = decodeURIComponent(this._instance.path);
            let docName = Path.basename(docPath, Path.extname(docPath));
            return docName ? docName : "Untitled";
        } catch (e) {
            BUtils.logInfo("Document not saved on the file system. Using name 'Untitled'.");
        }
        return "Untitled";
    }

    exportPath() {
        return this._metadata.metadata("jsonExportPath");
    }

    uiScaleFactor() {
        return this._metadata.metadata("uiScaleFactor");
    }

    setUiScaleFactor(factor) {
        const value = Number.parseFloat(factor);
        if (Number.isNaN(value) || !(value > 0 && value < (10 + Number.EPSILON))) {
            UI.alert('Invalid scale factor', 'Assign a valid scale factor between 0 - 10!')
            return;
        }
        this.setMetadata("uiScaleFactor", value.toFixed(2));
    }

    organizeInPages() {
        return this._metadata.metadata("organizeInPages");
    }

    setOrganizeInPages(enable) {
        this.setMetadata("organizeInPages", enable);
    }

    scaleFormat() {
        let scaleFormat = this._metadata.metadata("scaleFormat");
        let options = this._metadata.metadata("formatOptions");
        scaleFormat.forEach(sf => {
            if (sf.formats in options) {
                sf = Object.assign(sf, options[sf.formats]);
            }
        });
        return scaleFormat;
    }

    setFormatOptions(userOptions) {
        let options = this._metadata.metadata("formatOptions");
        let pngOptions = options[VEF.Png] ? options[VEF.Png] : {};
        pngOptions["save-for-web"] = userOptions.saveMetadata;
        options[VEF.Png] = pngOptions;

        let jpgOptions = options[VEF.Jpg] ? options[VEF.Jpg] : {};
        jpgOptions.progressive = userOptions.progressiveJpg;
        jpgOptions.compression = userOptions.compression;
        options[VEF.Jpg] = jpgOptions;

        let svgOptions = options[VEF.Svg] ? options[VEF.Svg] : {};
        svgOptions["include-namespaces"] = userOptions.includeNamespaces;
        svgOptions.compact = userOptions.compactSvg;
        options[VEF.Svg] = svgOptions;

        this.setMetadata("formatOptions", options);
    }

    setMetadata(name, value) {
        return this._metadata.setMetadata(name, value);
    }

    resetMetadata(name) {
        return this._metadata.resetMetadata(name);
    }

    documentInfo() {
        return {
            name: this.documentName(),
            metadata: this._metadata.allMetadata()
        };
    }

    normalizedDocumentInfo() {
        let info = this._metadata.allMetadata();
        info.name = this.documentName();
        return info;
    }

    symbolLibraries() {
        let result = {}
        let incompatibleLibs = new Map;
        this.forEachDocumentLayer(sLayer => {
            let libInstance = sLayer.libraryInstance()
            if (libInstance) {
                try {
                    let document = libInstance.getDocument();
                    if (!document) {
                        incompatibleLibs.set(libInstance.id, libInstance.name);
                    } else {
                        result[libInstance.getDocument().path] = libInstance;
                    }
                } catch (e) {
                    // Remote library do not have a valid document.
                    incompatibleLibs.set(libInstance.id, libInstance.name);
                }
            }
            return true;
        });

        for (let [id, name] of incompatibleLibs) {
            BUtils.logExportError(`Incompatible Symbol library: "${name}" Id: ${id}.`);
        }
        return Object.values(result);
    }

    haveRemoteSymbols() {
        let allLayerLocal = this.forEachDocumentLayer(sLayer => {
            // Return false to break loop when a library symbol is found.
            return !sLayer.libraryInstance();
        });
        return !allLayerLocal;
    }

    forEachArtboard(callback, progressCallback) {
        if (!callback || !this._instance || typeof callback !== "function") {
            return;
        }

        if (!progressCallback || typeof progressCallback !== "function") {
            progressCallback = p => { };
        }

        let pageShare = 1 / this._instance.pages.length;
        let done = 0.0;
        this._instance.pages.forEach(page => {
            if (!page.layers || page.layers.length === 0) {
                progressCallback((done += pageShare));
                return;
            }
            let artboardShare = pageShare / page.layers.length;
            if (page.layers) {
                page.layers.forEach((l, index) => {
                    let artboardLayer = new SLayer(l);
                    let lt = artboardLayer.layerType();
                    if (lt === SLT.Artboard || lt === SLT.SymbolMaster) {
                        callback(artboardLayer, index);
                    } else {
                        BUtils.logExportError(
                            `Unexpected layer ${artboardLayer.layerName()}.`,
                            ` Organize the layer under an Artboard`
                        );
                    }
                    progressCallback((done += artboardShare));
                });
            }
        });
    }

    // Return falsey from the callback to break the loop.
    forEachDocumentLayer(callback) {
        let iterateGroups = (sLayer) => {
            let success = callback(sLayer)
            if (success && sLayer.haveChildren()) {
                success = sLayer.instance().layers.every(childLayer => {
                    return iterateGroups(new SLayer(childLayer))
                });
            }
            return success;
        }

        return this._instance.pages.every(page => {
            if (page.layers) {
                return page.layers.every(l => { return iterateGroups(new SLayer(l)); });
            }
            return true;
        });
    }

    sanitize() { }
}

function defaultExportPath(instance) {
    var rootPath;
    try {
        rootPath = instance.sketchObject
            .fileURL()
            .path();
    } catch (e) {
        rootPath = "~/Documents/Untitled";
        BUtils.logInfo("Document not saved on the file system. Using default path", rootPath);
    }
    return Path.dirname(rootPath);
}

function defaultScaleFormat() {
    return [Object.assign({}, { formats: VEF.Png, scales: "1" }, CEO)];
}

function defaultUiScaleFactor() {
    return 1.0;
}

function defaultFormatOptions() {
    let formatOptions = {};
    formatOptions[VEF.Png] = {
        "save-for-web": false
    };
    formatOptions[VEF.Jpg] = {
        progressive: false,
        compression: 1.0
    };
    formatOptions[VEF.Svg] = {
        "include-namespaces": false,
        compact: true
    };
    return formatOptions;
}
